import {
  isStaticSite,
  dialog,
  fs,
  fse
} from "../electron-modules";
import slash from 'slash';
import { toast } from "../app";
import { cameraMatrixFilter } from "../file-extensions";
import { t } from "../localization";
import { Euler, Matrix4, Vector3 } from "three";

interface TransformResponse {
  success: Boolean,
  content: number[]
}

export const saveMatrixFile = (cameraTransformPath) => {
  dialog.showSaveDialog({
    filters: cameraMatrixFilter,
  }).then(result => {
    if (result.canceled) {
      return;
    }
    if (result.filePath) {
      const savePath = slash(result.filePath);
      fs.copyFile(cameraTransformPath, savePath, (err) => {
        if (err) {
          toast.error(t("toast.correction-matrix-failed"));
        } else {
          toast.success(t("toast.correction-matrix-saved"));
        }
      });
    }
  });
};

export const readTransformFile = (path: string): Promise<TransformResponse> => {
  return new Promise(async resolve => {
    const errorResponse = {
      success: false,
      content: null
    };

    if (!path) {
      resolve(errorResponse);
      return;
    }

    try {
      let data = isStaticSite ?
        await fetch(path).then(res => res.text()) :
        await fse.readFile(path, {encoding: 'utf8'});

      let arr = [];
      let valid = true;
      let rows = data.split("\n");

      rows.forEach(row => {
        row = row.trim();
        if (row.length === 0) {
          return;
        }

        let cols = row.split(" ");
        if (cols.length !== 4) {
          valid = false;
        }

        cols.forEach(col => {
          if (isNaN(col)) {
            valid = false;
          }
          arr.push(parseFloat(col));
        });
      });

      if (arr.length !== 16) {
        valid = false;
      }

      if (valid === false) {
        resolve(errorResponse);
        return;
      }

      const successResponse = {
        success: true,
        content: arr
      };

      resolve(successResponse);
      return;
    } catch {
      resolve(errorResponse);
      return;
    }
  });
};

export const getMatrixValues = async (path: string) => {
  const {success, content} = await readTransformFile(path);

  if (!success) {
    return {success, rotation: null, offset: null};
  }

  const matrix = new Matrix4().fromArray(content);

  const transpose = new Matrix4().copy(matrix).transpose();
  const offset = new Vector3().setFromMatrixPosition(transpose);

  matrix.setPosition(0,0,0);
  const rotation = new Euler()
    .setFromRotationMatrix(matrix)
    .toVector3()
    .multiplyScalar(180 / Math.PI)
    .multiplyScalar(-1);

  return {success, rotation, offset};
};
